/**
 * Central definition of Clerk organization role slugs and role helpers.
 *
 * Confirmed against Clerk PROD Kwick org: roles in use are
 *   - 'org:admin'      (Admin)
 *   - 'org:supervisor' (supervisor)
 *   - 'org:end_user'   (End-user — community member)
 *
 * Historically the codebase detected end users *negatively*
 * (`role !== 'org:admin' && role !== 'org:supervisor'`). Prefer the helpers
 * below so the end-user role is named explicitly and consistently.
 */

export const ORG_ROLES = {
  ADMIN: 'org:admin',
  SUPERVISOR: 'org:supervisor',
  END_USER: 'org:end_user',
} as const;

export type OrgRole = (typeof ORG_ROLES)[keyof typeof ORG_ROLES];

/** Roles that belong to platform staff (admin console users). */
export const STAFF_ROLES: readonly string[] = [
  ORG_ROLES.ADMIN,
  ORG_ROLES.SUPERVISOR,
];

/** All roles that are allowed to authenticate into the web app. */
export const WEB_ALLOWED_ROLES: readonly string[] = [
  ORG_ROLES.ADMIN,
  ORG_ROLES.SUPERVISOR,
  ORG_ROLES.END_USER,
];

/**
 * Extract the organization role from a Redux user object, handling the
 * various shapes the role can live under (`orgRole`, token content, metadata).
 * Use this as the single source of truth for "what role is this user?" so the
 * predicates below (and any ad-hoc checks) all read the role the same way.
 */
export function getOrgRole(user?: any): string | undefined {
  return (
    user?.orgRole ||
    user?.tokenContent?.org_role ||
    user?.tokenContent?.user_role ||
    user?.metadata?.org_role ||
    undefined
  );
}

export function isAdmin(role?: string | null): boolean {
  return role === ORG_ROLES.ADMIN;
}

export function isSupervisor(role?: string | null): boolean {
  return role === ORG_ROLES.SUPERVISOR;
}

/** Admin or supervisor — i.e. platform staff. */
export function isStaff(role?: string | null): boolean {
  return !!role && STAFF_ROLES.includes(role);
}

/** Community member end user. */
export function isEndUser(role?: string | null): boolean {
  return role === ORG_ROLES.END_USER;
}
